import { useEffect } from "react";
import { useGetList } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";

type ActivityEvent = {
  id: number;
  type: string;
  summary: string;
  entity_type: string | null;
  entity_id: number | null;
  project_id: number | null;
  created_at: string;
};

const linkFor = (e: ActivityEvent) => {
  switch (e.entity_type) {
    case "ticket":
      return `/tickets/${e.entity_id}/show`;
    case "project":
      return `/projects/${e.entity_id}/show`;
    default:
      return e.project_id ? `/projects/${e.project_id}/show` : null;
  }
};

/** Reads as a sentence: "task.completed" -> "task completed". */
const label = (type: string) => type.replace(".", " ");

const ago = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/**
 * What is happening across the hub, live.
 *
 * Rows arrive over Supabase Realtime rather than a poll, so this moves while
 * you are looking at it. RLS applies to a Realtime subscription exactly as it
 * does to a query: an admin holds dashboard.view_all and receives everything,
 * while anyone else is sent only their own actions and their own projects.
 * There is no filtering in this component, and there must not be.
 */
export const HubActivityFeed = () => {
  const queryClient = useQueryClient();

  const { data, isPending } = useGetList<ActivityEvent>("activity_events", {
    pagination: { page: 1, perPage: 25 },
    sort: { field: "created_at", order: "DESC" },
  });

  useEffect(() => {
    const channel = getSupabaseClient()
      .channel("hub-activity")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "activity_events" },
        () => {
          // Refetch rather than appending the payload: the row that arrives is
          // the one the subscriber is allowed to see, but the list also has
          // ordering and paging that the cache owns.
          queryClient.invalidateQueries({ queryKey: ["activity_events"] });
        },
      )
      .subscribe();

    return () => {
      getSupabaseClient().removeChannel(channel);
    };
  }, [queryClient]);

  if (isPending) return null;
  const events = data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          Live activity
          <span
            className="size-2 rounded-full bg-green-500"
            aria-label="Updating live"
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            Nothing yet. Tasks, tickets and project changes appear here as they
            happen.
          </p>
        ) : (
          <div className="divide-y max-h-96 overflow-y-auto">
            {events.map((event) => {
              const to = linkFor(event);
              const row = (
                <div className="py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Badge variant="outline" className="mb-1">
                      {label(event.type)}
                    </Badge>
                    <p className="text-sm truncate">{event.summary}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 pt-1">
                    {ago(event.created_at)}
                  </span>
                </div>
              );
              return to ? (
                <Link
                  key={event.id}
                  to={to}
                  className="block hover:bg-accent/40"
                >
                  {row}
                </Link>
              ) : (
                <div key={event.id}>{row}</div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
